import { v4 as uuidv4 } from "uuid";

import { AvailableRate } from "@/@types/Rating/AvailableRate";
import {
  centsToDecimal,
  V2ChargeLine,
  V2PricedQuote,
  V2RateShopResult,
} from "./v2GuestQuoteMapper";

/**
 * NEXT-GUEST-QUOTES-V2 — map the v2 `RateShopResult` (the 200 body of
 * `POST /v2/rating/quotes/guest`) into the UI's existing `AvailableRate[]` shape so the
 * `ChooseService` / `ChooseServiceTable` / `ChooseServiceTop3` components render unchanged.
 *
 * The big difference from v1's `getRateResponseMapper`:
 *   - v2 returns `{ quotes: [...], carrierErrors: [...] }` (two buckets). Per-carrier failures ride
 *     IN-BAND as `carrierErrors` and never abort the others — so this mapper returns only the
 *     successful `quotes[]`. The endpoint's empty-quotes cases (`no-carrier-accounts` 422,
 *     `all-carriers-unavailable` 502) are surfaced as RFC 7807 errors before this mapper runs.
 *   - money is integer cents → decimal for display (`centsToDecimal`); the v1 mapper read decimal
 *     `amount` fields straight off the wire.
 *   - the carrier/service/account are flat strings on v2 (`carrierCode`, `serviceCode`,
 *     `serviceName`, `carrierAccountId`) — v1 nested them under `shippingCarrierAccount` /
 *     `shippingService`. No `account.isActive` filter here: the guest tenant only ever returns active
 *     accounts (the v1 `.filter(rate => rate.account.isActive)` was a v1-ism for a flaky field).
 *   - transit-day / arrival-date are NOT in the v2 `PricedQuote` (rating is price-only; transit is
 *     a shipping/track concern). The UI's transit columns render empty for guest quotes — same as
 *     they did when v1 omitted them.
 */

/** Convert one v2 charge line to the UI's `{ name, amount, currency }` display shape. */
function toDisplayCharge(line: V2ChargeLine) {
  return {
    name: line.name,
    amount: centsToDecimal(line.amount?.cents),
    code: line.code,
    currency: line.amount?.currency ?? "CAD",
  };
}

/**
 * Map a v2 `RateShopResult` into the `AvailableRate[]` the rates UI already consumes.
 * Returns only `result.quotes` (the successful fan-out); per-carrier failures stay in
 * `result.carrierErrors` for the caller to decide how to surface.
 */
export function mapGuestQuoteResponse(
  result: V2RateShopResult
): AvailableRate[] {
  if (!result || !Array.isArray(result.quotes)) return [];

  return result.quotes.map((q: V2PricedQuote): AvailableRate => {
    const currency = q.currency ?? q.totalCharge?.currency ?? "CAD";

    return {
      id: uuidv4().replaceAll("-", ""),
      // v2 PricedQuote carries no transit data (price-only). The UI renders these as empty/0 —
      // consistent with how the v1 mapper left them when the carrier omitted transit.
      transitDay: 0,
      arrivalTime: "",
      arrivalDate: "",
      serviceOptionsInRate: [],
      carrierName: q.carrierCode,
      account: {
        // The string guid is the v2 carrier account id; the UI only uses `name`/`isActive` for
        // display. isActive stays true (the guest tenant never returns inactive accounts).
        id: 0,
        name: q.carrierCode,
        accountType: "",
        isActive: true,
      },
      service: {
        serviceCode: q.serviceCode,
        serviceName: q.serviceName,
        carrierName: q.carrierCode,
      },
      price: {
        currency,
        totalCharge: centsToDecimal(q.totalCharge?.cents),
        baseCharge: centsToDecimal(q.baseCharge?.cents),
        adjustments: (q.adjustments ?? []).map(toDisplayCharge),
        optionsCharge: [],
        additionalCharges: [],
        taxes: (q.taxes ?? []).map(toDisplayCharge),
      },
      // Guest compare-at display markup (+80%), if the backend attached one. The UI doesn't render
      // this today; carrying it keeps the field available for a future "was X" strike-through.
      baseCurrency: q.regularCompareAtCharge
        ? {
            currency: q.regularCompareAtCharge.currency ?? currency,
            amount: centsToDecimal(q.regularCompareAtCharge.cents),
          }
        : undefined,
    };
  });
}
